'use client';

import React, { useState, useCallback } from 'react';
import Link from 'next/link';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

export default function PricingCalculator() {
  const [emdAmount, setEmdAmount] = useState(250000);
  const [dueDiligenceDays, setDueDiligenceDays] = useState(30);
  // fee breakdown always visible

  const ADMIN_FEE = 2500;
  const RATE_PER_DAY_PER_1K = 1; // $1/day per $1,000 funded

  const dealFee = emdAmount / 1000 * RATE_PER_DAY_PER_1K * dueDiligenceDays;
  const totalCost = ADMIN_FEE + dealFee;
  const capitalKeptWorking = emdAmount - totalCost;
  const capitalPct = Math.max(0, Math.min(100, capitalKeptWorking / emdAmount * 100));
  const costPct = 100 - capitalPct;

  const handleEmdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmdAmount(Number(e.target.value));
  }, []);

  const handleDaysChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDueDiligenceDays(Number(e.target.value));
  }, []);

  return (
    <section className="px-6 pb-10" id="pricing-calculator" aria-label="Pricing calculator">
      <div className="max-w-6xl mx-auto">
        {/* Section label */}
        <div className="mb-8 px-2 py-6">
          <div className="relative">
            <span
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] px-3 py-1.5 rounded-full mb-4"
              style={{ background: 'rgba(82,183,136,0.12)', color: 'var(--color-accent)' }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: 'var(--color-accent)' }} />
              Interactive Calculator
            </span>
            <h2
              className="font-display"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
                color: 'var(--color-foreground)',
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                fontWeight: 700
              }}>
              Earnest Bridge Keeps Your{' '}
              <span style={{ color: 'var(--color-accent)' }}>Capital Working</span>
            </h2>
            <p className="mt-2 text-sm max-w-lg" style={{ color: 'var(--color-muted)', lineHeight: 1.6 }}>
              Adjust the sliders below to see your exact cost — no surprises, no hidden fees.
            </p>
          </div>
        </div>

        {/* Calculator card */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'var(--color-primary-dark)',
            border: '1px solid rgba(82,183,136,0.15)'
          }}>
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* ── Left Panel: Inputs ── */}
            <div
              className="p-8 md:p-10 flex flex-col gap-8"
              style={{ borderRight: '1px solid rgba(82,183,136,0.1)' }}>

              {/* EMD Amount */}
              <div>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em] mb-4"
                  style={{ color: 'var(--color-accent)' }}>
                  1. EMD Amount
                </p>
                <p
                  className="font-display leading-none mb-1"
                  style={{
                    fontSize: 'clamp(2.8rem, 6vw, 4rem)',
                    color: 'white',
                    letterSpacing: '-0.04em'
                  }}>
                  {formatCurrency(emdAmount)}
                </p>
                <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>Drag slider to adjust EMD amount

                </p>

                {/* Slider */}
                <div className="relative">
                  <input
                    type="range"
                    min={25000}
                    max={1000000}
                    step={5000}
                    value={emdAmount}
                    onChange={handleEmdChange}
                    className="calc-slider w-full"
                    aria-label="EMD amount" />

                  <div className="flex justify-between mt-2">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>$25,000</span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>$1,000,000</span>
                  </div>
                </div>
              </div>

              {/* Due Diligence Period */}
              <div>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em] mb-4"
                  style={{ color: 'var(--color-accent)' }}>
                  2. Due Diligence Period
                </p>
                <p
                  className="font-display leading-none mb-1"
                  style={{
                    fontSize: 'clamp(2.8rem, 6vw, 4rem)',
                    color: 'white',
                    letterSpacing: '-0.04em'
                  }}>
                  {dueDiligenceDays} <span className="text-2xl font-sans font-light" style={{ color: 'rgba(255,255,255,0.5)' }}>days</span>
                </p>
                <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>Drag slider to adjust due diligence period

                </p>

                {/* Slider */}
                <div className="relative">
                  <input
                    type="range"
                    min={7}
                    max={90}
                    step={1}
                    value={dueDiligenceDays}
                    onChange={handleDaysChange}
                    className="calc-slider w-full"
                    aria-label="Due diligence period in days" />

                  <div className="flex justify-between mt-2">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>7 days</span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>90 days</span>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 pt-2">
                {['100% EMD Funded', 'No Capital Required', 'Close More Deals'].map((badge) =>
                <div key={badge} className="flex items-center gap-1.5">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="rgba(82,183,136,0.6)" />
                      <path d="M4.5 7L6.5 9L9.5 5.5" stroke="#52B788" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>{badge}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Right Panel: Results ── */}
            <div className="p-8 md:p-10 flex flex-col gap-6">

              {/* Capital You Keep Working */}
              <div>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.28em] mb-3"
                  style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Capital You Keep Working
                </p>
                <p
                  className="font-display leading-none"
                  style={{
                    fontSize: 'clamp(2.6rem, 5vw, 3.8rem)',
                    color: 'var(--color-accent-light)',
                    letterSpacing: '-0.04em'
                  }}>
                  {capitalKeptWorking > 0 ? formatCurrency(capitalKeptWorking) : '$0'}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Available for your next acquisition
                </p>
              </div>

              {/* Capital vs Cost bar */}
              <div>
                <div
                  className="rounded-xl overflow-hidden flex h-12"
                  style={{ background: 'rgba(255,255,255,0.06)' }}>
                  {/* Capital portion */}
                  <div
                    className="flex items-center justify-center transition-all duration-500"
                    style={{
                      width: `${capitalPct}%`,
                      background: 'rgba(82,183,136,0.25)',
                      borderRight: '1px solid rgba(82,183,136,0.3)'
                    }}>
                    {capitalPct > 20 &&
                    <div className="text-center px-2">
                        <p className="text-xs font-bold" style={{ color: 'var(--color-accent-light)' }}>
                          {capitalPct.toFixed(1)}%
                        </p>
                        <p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          Your Capital
                        </p>
                      </div>
                    }
                  </div>
                  {/* Cost portion */}
                  <div
                    className="flex items-center justify-center transition-all duration-500"
                    style={{
                      width: `${costPct}%`,
                      background: 'rgba(201,168,76,0.15)'
                    }}>
                    {costPct > 8 &&
                    <div className="text-center px-2">
                        <p className="text-xs font-bold" style={{ color: 'var(--color-gold)' }}>
                          {costPct.toFixed(1)}%
                        </p>
                        <p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          Cost to Secure
                        </p>
                      </div>
                    }
                  </div>
                </div>
              </div>

              {/* Fee Breakdown - always expanded */}
              <div
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(82,183,136,0.15)' }}>
                <div
                  className="w-full flex items-center px-5 py-4"
                  style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <span
                    className="text-[11px] font-bold uppercase tracking-[0.22em]"
                    style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Fee Breakdown
                  </span>
                </div>

                <div
                  className="px-5 pb-5 pt-1 flex flex-col gap-0"
                  style={{ borderTop: '1px solid rgba(82,183,136,0.1)' }}>
                    {[
                  {
                    label: 'Administrative Fee',
                    value: formatCurrency(ADMIN_FEE),
                    note: 'Flat fee · per deal'
                  },
                  {
                    label: `Deal Fee (${dueDiligenceDays} days)`,
                    value: formatCurrency(dealFee),
                    note: `$1/day × ${formatCurrency(emdAmount / 1000).replace('$', '')}K × ${dueDiligenceDays} days`
                  },
                  {
                    label: 'Total Cost',
                    value: formatCurrency(totalCost),
                    note: 'All-in · no hidden fees',
                    bold: true
                  }].
                  map((row, i, arr) =>
                  <div
                    key={row.label}
                    className="flex items-center justify-between py-3"
                    style={{
                      borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none'
                    }}>
                        <div>
                          <p
                        className={`text-sm ${row.bold ? 'font-bold' : 'font-medium'}`}
                        style={{ color: row.bold ? 'white' : 'rgba(255,255,255,0.7)' }}>
                            {row.label}
                          </p>
                          <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                            {row.note}
                          </p>
                        </div>
                        <p
                      className={`font-display ${row.bold ? 'text-xl' : 'text-lg'}`}
                      style={{
                        color: row.bold ? 'var(--color-gold)' : 'rgba(255,255,255,0.8)',
                        letterSpacing: '-0.02em'
                      }}>
                          {row.value}
                        </p>
                      </div>
                  )}
                  </div>
              </div>

              {/* CTA */}
              <Link
                href="/contact"
                className="flex items-center justify-center gap-2 rounded-full font-bold text-sm uppercase tracking-[0.15em] py-4 px-8 transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5"
                style={{ background: 'var(--color-accent)', color: 'white' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2L8 14M2 8L14 8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Apply for EMD Funding
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Slider styles */}
      <style>{`
        .calc-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 2px;
          background: rgba(255,255,255,0.12);
          outline: none;
          cursor: pointer;
        }
        .calc-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--color-accent);
          border: 3px solid var(--color-primary-dark);
          box-shadow: 0 0 0 2px var(--color-accent);
          cursor: pointer;
          transition: box-shadow 0.2s;
        }
        .calc-slider::-webkit-slider-thumb:hover {
          box-shadow: 0 0 0 5px rgba(82,183,136,0.25);
        }
        .calc-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--color-accent);
          border: 3px solid var(--color-primary-dark);
          box-shadow: 0 0 0 2px var(--color-accent);
          cursor: pointer;
        }
        .calc-slider::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 2px;
        }
      `}</style>
    </section>);

}